"use client";

import { useEffect, useState } from "react";

/**
 * The "Something went wrong" panel, extracted so two boundaries can render the
 * SAME thing.
 *
 * `app/error.tsx` is Next's route-segment boundary and has always drawn this.
 * `ComponentStackBoundary` sits inside the app shell and now catches first -
 * it has to, because a React boundary is the only surface that receives
 * `errorInfo.componentStack`. Two boundaries drawing two different panels
 * would be a difference the lifter can see for no reason they could name, so
 * they draw one.
 */

/** What the probe found. `unknown` is the first paint, before the answer is
 *  back - and it renders the generic copy rather than a spinner, because a
 *  spinner on an error screen is a second thing going wrong. */
type Health = "unknown" | "up" | "down";

export function ErrorFallback({
  onRetry,
  inShell = false,
}: {
  onRetry: () => void;
  /** True when this renders INSIDE the app shell - i.e. from
   *  ComponentStackBoundary, with the fixed header above it and the TabBar's
   *  reserved padding below.
   *
   *  It exists because the panel used to centre itself in `min-h-dvh` from
   *  both mounts, and inside the shell a full VIEWPORT height starts 52px down
   *  and runs past the tab bar - so the "centred" card sat visibly low, which
   *  is exactly what it looked like on a phone. Outside the shell (the root
   *  boundary, where the chrome is gone) the viewport height is right. */
  inShell?: boolean;
}) {
  const [health, setHealth] = useState<Health>("unknown");

  useEffect(() => {
    // Is the server there at all?
    //
    // A deploy restarts the Node process, nginx answers 502 for a few seconds,
    // and every navigation in that window lands here looking exactly like a
    // bug. It is not one, and telling a lifter their app crashed when it is
    // being updated is both wrong and alarming.
    //
    // The client cannot tell the two apart from the failure it caught, but it
    // can ask whether the server is answering now. /api/health does nothing
    // but return 200, so a non-ok response or a thrown fetch means the process
    // is down rather than that this page is broken.
    let alive = true;
    const t = setTimeout(() => {
      if (alive) setHealth("down");
    }, 4000);
    fetch("/api/health", { cache: "no-store" })
      .then((r) => {
        if (!alive) return;
        // 502/503/504 from nginx while PM2 restarts, or anything else not ok.
        setHealth(r.ok ? "up" : "down");
      })
      .catch(() => {
        if (alive) setHealth("down");
      })
      .finally(() => clearTimeout(t));
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, []);

  const maintenance = health === "down";

  return (
    <main
      className={
        "flex items-center justify-center px-4 " +
        // Inside the shell, subtract the chrome this panel is sitting between:
        // the header (h-11, 2.75rem) plus the page wrapper's own pt-2 (0.5rem)
        // and its pb reservation for the TabBar (5rem). sm: the header grows to
        // h-14 (3.5rem), pt-4 (1rem) and the reservation to 6rem.
        (inShell
          ? "min-h-[calc(100dvh-8.25rem)] sm:min-h-[calc(100dvh-10.5rem)]"
          : "min-h-dvh")
      }
    >
      <div className="w-full max-w-sm rounded-lg border border-border bg-surface p-5 flex flex-col gap-4">
        <div>
          <h1 className="text-fg text-base font-semibold">
            {maintenance ? "OutRepR is updating" : "Something went wrong"}
          </h1>
          <p className="text-fg-muted text-sm mt-1">
            {maintenance
              ? // Names the cause, gives the shape of the wait, and says the
                // one thing a lifter actually wants to know mid-session.
                "A new version is going out, so the app is unavailable for a moment. Nothing has been lost - your data is on the server. Try again shortly."
              : "An unexpected error stopped this page from loading. Your data is saved on the server - trying again usually fixes it."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onRetry}
            className="h-9 px-4 rounded-md bg-brand text-on-brand hover:bg-brand-strong text-sm font-semibold transition-colors"
          >
            Try again
          </button>
          {/* Hidden while the server is down: every other route is served by
              the same process, so offering a second door into a building that
              is not answering is a second failure one tap away. */}
          {!maintenance && (
            <a
              href="/dashboard"
              className="h-9 px-3 rounded-md text-fg-muted hover:text-fg text-sm flex items-center transition-colors"
            >
              Go to Dashboard
            </a>
          )}
        </div>
      </div>
    </main>
  );
}
